import {
  BankAccountHealthEnum,
  PaymentAccountTransferMethod,
  type BankAccountFragmentFragment,
} from '@graphql/react-apollo'

import type { AttentionIssue } from '@web-app/components/account/attention/types'
import {
  isRelinkableCard,
  isUsableCard,
} from '@web-app/components/payment-account-management/card/lib/cardPredicates'
import { findLinkedAccount } from '@web-app/components/payment-account-management/lib/accountMatching'

// Rail health off the server's signal, and the issues it raises. Every
// surface reads from here, so the copy lives here.

export const LINKED_ACCOUNTS_ROUTE = '/dashboard/account/linked-accounts'

export type AchState =
  | { kind: 'none' }
  | {
      kind: 'linked'
      account: BankAccountFragmentFragment
      // Update mode fixes it. The server sets flag and health together.
      needsReauth: boolean
      // Item is gone; only a fresh link helps.
      unrepairable: boolean
    }

// The server's own vocabulary, narrowed to what a card can carry; null is
// healthy. REQUIRE_REAUTH and UNREPAIRABLE are Plaid-item states. An expiring
// card still charges today; an expired or inoperable one doesn't.
export type CardHealth =
  | BankAccountHealthEnum.Expired
  | BankAccountHealthEnum.ExpiringSoon
  | BankAccountHealthEnum.Inop
  | null

export type CardState =
  | { kind: 'none' }
  | { kind: 'linked'; card: BankAccountFragmentFragment; health: CardHealth }
  // Previously-linked cards the user can reactivate.
  | { kind: 'relinkable'; cards: BankAccountFragmentFragment[] }

export function resolveAchState(
  accounts: ReadonlyArray<BankAccountFragmentFragment>,
): AchState {
  const account = findLinkedAccount(
    [...accounts],
    PaymentAccountTransferMethod.Ach,
  )
  if (!account) return { kind: 'none' }
  return {
    kind: 'linked',
    account,
    needsReauth:
      account.requireReauth === true ||
      account.health === BankAccountHealthEnum.RequireReauth,
    unrepairable: account.health === BankAccountHealthEnum.Unrepairable,
  }
}

// EXPIRED / EXPIRING_SOON: update it. INOP: dead, needs a new card.
function cardHealth(card: BankAccountFragmentFragment): CardHealth {
  switch (card.health) {
    case BankAccountHealthEnum.Expired:
    case BankAccountHealthEnum.ExpiringSoon:
    case BankAccountHealthEnum.Inop:
      return card.health
    default:
      return null
  }
}

export function resolveCardState(
  accounts: ReadonlyArray<BankAccountFragmentFragment>,
): CardState {
  const card = accounts.find(isUsableCard)
  if (card) return { kind: 'linked', card, health: cardHealth(card) }
  const relinkable = accounts.filter(isRelinkableCard)
  if (relinkable.length > 0) return { kind: 'relinkable', cards: relinkable }
  return { kind: 'none' }
}

export function cardHealthCopy(
  card: BankAccountFragmentFragment,
  health: CardHealth,
): string | null {
  const ending = card.displayAccountNumber
    ? `Your card ending in ${card.displayAccountNumber}`
    : 'Your card'
  switch (health) {
    case BankAccountHealthEnum.ExpiringSoon:
      return `${ending} is expiring soon.`
    case BankAccountHealthEnum.Expired:
      return `${ending} has expired.`
    case BankAccountHealthEnum.Inop:
      return `${ending} can no longer be used. Please link a new card.`
    default:
      return null
  }
}

// The rails' issues for the user. critical: money can't move now; warning:
// it will soon.
export function paymentAccountIssues(
  accounts: ReadonlyArray<BankAccountFragmentFragment>,
): AttentionIssue[] {
  const issues: AttentionIssue[] = []

  const ach = resolveAchState(accounts)
  if (ach.kind === 'linked' && ach.unrepairable) {
    issues.push({
      kind: 'bankUnrepairable',
      domain: 'paymentAccounts',
      accountId: ach.account.id,
      severity: 'critical',
      title: 'Bank connection lost',
      body: 'Your bank can no longer be reached. Link it again to keep transfers working.',
      action: {
        type: 'link',
        label: 'Link a bank',
        href: LINKED_ACCOUNTS_ROUTE,
      },
    })
  } else if (ach.kind === 'linked' && ach.needsReauth) {
    issues.push({
      kind: 'bankReauth',
      domain: 'paymentAccounts',
      accountId: ach.account.id,
      severity: 'warning',
      title: 'Bank re-link required',
      body: 'Re-link your bank to prevent service interruptions.',
      action: {
        type: 'plaidUpdate',
        label: 'Re-link now',
        paymentAccountId: ach.account.id,
      },
    })
  }

  const card = resolveCardState(accounts)
  if (card.kind === 'linked' && card.health !== null) {
    const body = cardHealthCopy(card.card, card.health) ?? ''
    issues.push(
      card.health === BankAccountHealthEnum.Inop
        ? {
            kind: 'cardInop',
            domain: 'paymentAccounts',
            accountId: card.card.id,
            severity: 'critical',
            title: 'Card update needed',
            body,
            action: {
              type: 'link',
              label: 'Link a new card',
              href: LINKED_ACCOUNTS_ROUTE,
            },
          }
        : {
            // One alert for both; only the grade differs. An expired card
            // can't be charged today, an expiring one still can.
            kind: 'cardExpiring',
            domain: 'paymentAccounts',
            accountId: card.card.id,
            severity:
              card.health === BankAccountHealthEnum.Expired
                ? 'critical'
                : 'warning',
            title: 'Card update needed',
            body,
            action: {
              type: 'link',
              label: 'Update card',
              href: LINKED_ACCOUNTS_ROUTE,
            },
          },
    )
  }

  return issues
}
